import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import dayjs from "dayjs";
import { api } from "../api";
import { flushQueue, isNetworkError, newClientKey, queueAdd } from "../offlineQueue";
import { fromCents, toCents } from "../money";
import { parseSearch, searchMatches } from "../txSearch";
import type {
  CardPayment,
  Category,
  Owner,
  Period,
  Suggestion,
  Transaction,
  TransactionInput,
  TransactionBulkUpdate,
  TransactionPage,
  TxSource,
} from "../types";
import { keys } from "./keys";
import { invalidateMoney, useMoneyMutation } from "./money";
import { cardsQuery, dashboardQuery } from "./views";

// ---------------------------------------------------------------- transactions

export interface TxFilters {
  period?: string;
  /** ISO dates; either one set overrides `period` (both inclusive, an empty side is open). */
  start?: string;
  end?: string;
  owner?: Owner;
  account_id?: number;
  category_id?: number;
  /** words, an amount ("850") or a range ("800-900"): `lib/txSearch` */
  q?: string;
  kind?: string[];
  /** only MSI purchases */
  msi?: boolean;
  source?: TxSource;
}

const transactionsQuery = (f: TxFilters) =>
  queryOptions({
    queryKey: keys.transactions(f),
    queryFn: () =>
      api.get<TransactionPage>("/transactions", { ...f, limit: 300 } as Record<
        string,
        string | number | boolean | string[] | undefined
      >),
  });

export const useTransactions = (f: TxFilters) =>
  useQuery({ ...transactionsQuery(f), placeholderData: (prev) => prev });

/** Rows per page of the Gastos list; the next page loads as you scroll near the end. */
export const TX_PAGE_SIZE = 200;

export type TxPages = InfiniteData<TransactionPage, string | undefined>;

/** Gastos: pages chained by the keyset cursor, so a row added mid-scroll never shifts them. */
const transactionPagesQuery = (f: TxFilters) =>
  infiniteQueryOptions({
    queryKey: keys.transactionPages(f),
    queryFn: ({ pageParam }) =>
      api.get<TransactionPage>("/transactions", {
        ...f,
        limit: TX_PAGE_SIZE,
        before: pageParam,
      } as Record<string, string | number | boolean | string[] | undefined>),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });

export const useTransactionPages = (f: TxFilters) =>
  useInfiniteQuery({ ...transactionPagesQuery(f), placeholderData: (prev) => prev });

/** The rows the filters keep, as a CSV file on the phone or computer (#48). */
export async function downloadTransactionsCsv(f: TxFilters): Promise<void> {
  const blob = await api.downloadBlob("/transactions/export", { ...f });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `movimientos-${dayjs().format("YYYY-MM-DD")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Money views a bulk edit can't change (#48): a category or an owner doesn't move any balance,
 * debt or the cuenta principal. MSI purchases carry their own fixed payment, which follows
 * their category, owner and account.
 */
export function bulkUntouched(
  change: Omit<TransactionBulkUpdate, "ids">,
  msi: boolean,
): QueryKey[] {
  if (change.account_id !== undefined) return [["debts"], ...(msi ? [] : [keys.budgetItems])];
  const keep: QueryKey[] = [["accounts"], ["debts"], ["pot"], ["calendar"]];
  if (!msi) keep.push(["msi"], keys.budgetItems);
  // who pays a card statement depends on the owner, not on the category (ADR 29)
  if (change.owner === undefined) keep.push(["cards"]);
  return keep;
}

/** Gastos → Seleccionar: change the category, owner or account of many rows at once. */
export function useBulkUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      change,
      ids,
    }: {
      change: Omit<TransactionBulkUpdate, "ids">;
      ids: number[];
      msi: boolean;
    }) => api.patch<{ count: number }>("/transactions/bulk", { ...change, ids }),
    onSuccess: (_data, { change, msi }) => void invalidateMoney(qc, bulkUntouched(change, msi)),
  });
}

/** Gastos → Seleccionar → Borrar: every balance can move, so everything refreshes. */
export function useBulkDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      api.post<{ count: number }>("/transactions/bulk-delete", { ids }),
    onSuccess: () => void invalidateMoney(qc),
  });
}

/** Start loading a tab's main data on press-in / hover, before the tap completes. */
export function usePrefetchTab(): (to: string) => void {
  const qc = useQueryClient();
  return (to) => {
    if (to === "/") void qc.prefetchQuery(dashboardQuery());
    else if (to === "/tarjetas") void qc.prefetchQuery(cardsQuery());
    else if (to === "/gastos") {
      // Gastos opens on the latest periodo (same filters, so the same cache entry)
      const period = qc.getQueryData<Period[]>(keys.periods)?.[0]?.key;
      if (period) void qc.prefetchInfiniteQuery(transactionPagesQuery({ period }));
    }
  };
}

type TxListSnapshot = [QueryKey, TransactionPage | TxPages | undefined][];

/**
 * Quick add: the new row shows in every cached list it belongs to right away, and money views
 * refresh in the background, so the sheet can close as soon as you tap Guardar. If the server
 * rejects it, the lists go back to what they were and `onError` reports it.
 */
export function useAddTransaction(options: {
  onError?: (err: Error, t: TransactionInput) => void;
  /** no signal: it waits on the phone and is sent when the connection is back (#42) */
  onQueued?: (t: TransactionInput) => void;
}) {
  const qc = useQueryClient();
  return useMutation<Transaction | null, Error, TransactionInput, { snapshot: TxListSnapshot }>({
    // runs offline too (React Query would pause it in memory): it queues instead
    networkMode: "always",
    mutationFn: async (t) => {
      const body = { ...t, client_key: t.client_key ?? newClientKey() };
      if (!navigator.onLine) return queueAdd(body).then(() => null);
      try {
        return await api.post<Transaction>("/transactions", body);
      } catch (err) {
        // it may have reached the server: the same key makes the retry a no-op there
        if (isNetworkError(err)) return queueAdd(body).then(() => null);
        throw err;
      }
    },
    onMutate: async (t) => {
      await qc.cancelQueries({ queryKey: ["transactions"] });
      const snapshot = qc.getQueriesData<TransactionPage | TxPages>({
        queryKey: ["transactions"],
      });
      const row = optimisticRow(t);
      const categories = qc.getQueryData<Category[]>(keys.categories) ?? [];
      for (const [key, data] of snapshot) {
        const filters = key[1];
        if (!data || typeof filters !== "object" || filters === null) continue;
        if (!listShows(filters as TxFilters, row, categories)) continue;
        qc.setQueryData(key, "pages" in data ? withRowInPages(data, row) : withRow(data, row));
      }
      return { snapshot };
    },
    // a queued row stays in the lists (still dimmed) until it's sent
    onSuccess: (saved, t) => {
      if (saved === null) options.onQueued?.(t);
    },
    onError: (err, t, ctx) => {
      for (const [key, page] of ctx?.snapshot ?? []) qc.setQueryData(key, page);
      options.onError?.(err, t);
    },
    // an MSI purchase creates or links its own budget item; anything else can't change them
    onSettled: (_data, _err, t) => invalidateMoney(qc, t.msi_months ? [] : [keys.budgetItems]),
  });
}

let optimisticId = 0;

/** The row as the list will show it; a negative id marks it as not saved yet. */
function optimisticRow(t: TransactionInput): Transaction {
  return {
    id: --optimisticId,
    date: t.date,
    description: t.description,
    amount: t.amount,
    kind: t.kind ?? "expense",
    account_id: t.account_id,
    category_id: t.category_id ?? null,
    owner: t.owner ?? "BOTH",
    msi_months: t.msi_months ?? null,
    msi_start: t.msi_start ?? null,
    refund_of_id: t.refund_of_id ?? null,
    budget_item_id: t.budget_item_id ?? null,
    income_id: t.income_id ?? null,
    notes: t.notes ?? null,
    // the server's rule may mark it; the refetch after saving brings its answer
    extraordinary: t.extraordinary ?? false,
    counterpart_account_id: null,
    source: "manual",
    created_at: new Date().toISOString(),
  };
}

/** Periodo `YYYY-MM` runs from the 16th of that month to the 15th of the next one. */
export function periodRange(key: string | undefined): [string, string] {
  const start = key
    ? dayjs(`${key}-16`)
    : dayjs().date() >= 16
      ? dayjs().date(16)
      : dayjs().subtract(1, "month").date(16);
  return [start.format("YYYY-MM-DD"), start.add(1, "month").date(15).format("YYYY-MM-DD")];
}

/** Whether `GET /transactions` with these filters would return the row (same rules as the API). */
export function listShows(f: TxFilters, t: Transaction, categories: Category[]): boolean {
  const [start, end] = f.start || f.end ? [f.start, f.end] : periodRange(f.period);
  if ((start && t.date < start) || (end && t.date > end)) return false;
  if (f.owner && t.owner !== f.owner) return false;
  if (f.account_id && t.account_id !== f.account_id) return false;
  if (f.kind?.length && !f.kind.includes(t.kind)) return false;
  if (f.msi && !t.msi_months) return false;
  if (f.source && t.source !== f.source) return false;
  if (!searchMatches(parseSearch(f.q), t.description, t.amount)) return false;
  if (f.category_id) {
    const parent = categories.find((c) => c.id === t.category_id)?.parent_id;
    if (t.category_id !== f.category_id && parent !== f.category_id) return false;
  }
  return true;
}

/** The page with the row in place (newest date first, newest row first within a day). */
export function withRow(page: TransactionPage, t: Transaction): TransactionPage {
  const at = page.items.findIndex((x) => x.date <= t.date);
  const items = [...page.items];
  items.splice(at === -1 ? items.length : at, 0, t);
  const spends = t.kind === "expense" || t.kind === "refund";
  return {
    ...page,
    items,
    total: page.total + 1,
    sum: spends ? fromCents(toCents(page.sum) + toCents(t.amount)) : page.sum,
  };
}

/**
 * The loaded pages with the row in place: it goes into the first page that reaches its date
 * (or the last page when nothing more is left to load), and every page's total and sum count
 * it. A row older than everything loaded stays out until its page loads from the server.
 */
export function withRowInPages(data: TxPages, t: Transaction): TxPages {
  const last = data.pages.length - 1;
  let target = data.pages.findIndex((p) => p.items.some((x) => x.date <= t.date));
  if (target === -1 && data.pages[last]?.next == null) target = last;
  const pages = data.pages.map((page, i) => {
    const counted = withRow(page, t);
    return i === target ? counted : { ...counted, items: page.items };
  });
  return { ...data, pages };
}

export const useSaveTransaction = () =>
  useMoneyMutation((t: Partial<TransactionInput> & { id?: number }) =>
    t.id
      ? api.patch<Transaction>(`/transactions/${t.id}`, t)
      : api.post<Transaction>("/transactions", t),
  );

/** Send the movements added without signal: on start and whenever the connection comes back. */
export const sendQueuedAdds = (qc: QueryClient) =>
  flushQueue((input) => api.post<Transaction>("/transactions", input)).then((result) => {
    if (result.saved.length || result.rejected.length) void invalidateMoney(qc);
    return result;
  });

export const useDeleteTransaction = () =>
  useMoneyMutation((id: number) => api.delete(`/transactions/${id}`));

export const useTransfer = () =>
  useMoneyMutation(
    (v: {
      from_account_id: number;
      to_account_id: number;
      amount: string;
      date: string;
      description?: string;
    }) => api.post<Transaction[]>("/transactions/transfer", v),
  );

export const usePayCard = () =>
  useMoneyMutation((v: CardPayment) => api.post<Transaction[]>("/transactions/card-payment", v));

/** Category and owner for a description: a categorization rule for the account, else history. */
/** One movement, e.g. to edit it from somewhere other than Gastos (#46). */
export const fetchTransaction = (id: number) => api.get<Transaction>(`/transactions/${id}`);

export const fetchSuggestion = (description: string, accountId?: number) =>
  api.get<Suggestion | null>("/transactions/suggest", { description, account_id: accountId });
